import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { CalendarDays, Clock } from 'lucide-react';
import { format, addDays } from 'date-fns';

// Doctors think in "review in N days" not "pick a date on the calendar" —
// surgical follow-up at 7 days, oncology at 21, post-op review at 3, etc.
// The picker now defaults to a Days input + a few quick chips. The actual
// date is auto-computed and shown read-only; the calendar mode is gone
// because allowing a free date pick was producing review dates inconsistent
// with the prescription duration (e.g. 30-day course + review on day 15).

const QUICK_OPTIONS = [
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
];

const TIME_SLOTS = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM',
  '04:30 PM', '05:00 PM'
];

export function ReviewDatePicker({ value, onChange }) {
  const [days, setDays] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Hydrate from existing value: if a saved string like "2026-06-05" comes
  // in, derive how many days from today it is so the doctor sees the
  // original number rather than just a date.
  useEffect(() => {
    if (!value) return;
    try {
      const datePart = String(value).split(' ')[0];
      const timePart = String(value).split(' ').slice(1).join(' ');
      const dateObj = new Date(datePart);
      if (!isNaN(dateObj.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(dateObj);
        target.setHours(0, 0, 0, 0);
        const diffDays = Math.round((target - today) / 86400000);
        if (diffDays > 0) setDays(String(diffDays));
      }
      if (timePart && TIME_SLOTS.includes(timePart)) {
        setSelectedTime(timePart);
      }
    } catch (e) {
      console.error('Invalid review value:', e);
    }
  }, [value]);

  // The computed review date — derived solely from the doctor's day count
  // so the two stay in lockstep. Empty / 0 / non-numeric → no date.
  const computedDate = useMemo(() => {
    const n = parseInt(days, 10);
    if (!n || n <= 0) return null;
    return addDays(new Date(), n);
  }, [days]);

  // Push the structured "yyyy-MM-dd HH:mm AM/PM" string to the parent on
  // every change. We only emit when there's actually a positive day count
  // so an empty input doesn't accidentally save a date.
  useEffect(() => {
    if (!computedDate) {
      onChange('');
      return;
    }
    let dateStr = format(computedDate, 'yyyy-MM-dd');
    if (selectedTime) dateStr += ` ${selectedTime}`;
    onChange(dateStr);
  }, [computedDate, selectedTime, onChange]);

  const handleQuickSelect = (option) => setDays(String(option.days));

  const handleDaysChange = (e) => {
    // Numeric only, max 730 days (2 years) — anything longer is almost
    // certainly a typo. Empty allowed so the doctor can clear it.
    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    const n = parseInt(v, 10);
    if (v === '') {
      setDays('');
    } else if (!isNaN(n) && n >= 0 && n <= 730) {
      setDays(String(n));
    }
  };

  const currentDays = parseInt(days, 10) || 0;

  return (
    <div className="space-y-6" data-testid="review-date-picker">
      {/* Days entry — the canonical input */}
      <div className="space-y-2">
        <Label htmlFor="review-days-input" className="text-sm font-medium flex items-center gap-2">
          <CalendarDays className="h-4 w-4" /> Review in (days)
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="review-days-input"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 7"
            value={days}
            onChange={handleDaysChange}
            className="w-32 text-base font-semibold"
            data-testid="review-days-input"
            autoComplete="off"
          />
          <span className="text-sm text-slate-500">days from today</span>
        </div>
      </div>

      {/* Quick chips — surgical/oncology/post-op typical follow-up intervals */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Quick presets</Label>
        <div className="flex flex-wrap gap-2">
          {QUICK_OPTIONS.map((option) => {
            const isActive = currentDays === option.days;
            return (
              <Button
                key={option.label}
                variant="outline"
                size="sm"
                onClick={() => handleQuickSelect(option)}
                className={isActive ? 'bg-blue-50 border-blue-300 text-blue-800 font-semibold' : ''}
                data-testid={`quick-select-${option.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Time slot — independent of date so the doctor can pin the
          appointment time without changing the day count. */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4" /> Time slot (optional)
        </Label>
        <Select value={selectedTime} onValueChange={setSelectedTime}>
          <SelectTrigger data-testid="time-select" className="w-64">
            <SelectValue placeholder="Choose a time slot" />
          </SelectTrigger>
          <SelectContent>
            {TIME_SLOTS.map((time, index) => (
              <SelectItem key={index} value={time} data-testid={`time-option-${index}`}>
                {time}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Computed-date summary — the doctor sees the resulting date but
          can't pick a different one. Keeps the "review date" semantically
          tied to the day count entered. */}
      {computedDate ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="review-summary">
          <h4 className="text-xs font-semibold text-blue-900 mb-1 uppercase tracking-wide">Next review</h4>
          <p className="text-lg font-bold text-blue-900">
            {format(computedDate, 'EEEE, MMMM d, yyyy')}
            {selectedTime && <span className="ml-1 text-blue-800 font-semibold">at {selectedTime}</span>}
          </p>
          <p className="text-xs text-blue-700 mt-0.5">
            {currentDays} day{currentDays !== 1 ? 's' : ''} from today
          </p>
        </div>
      ) : (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-4 text-center text-sm text-slate-500" data-testid="review-empty-state">
          Enter a number of days to set the review date
        </div>
      )}
    </div>
  );
}
